import type { ChatEditSchema } from '@repo/schemas/chat/chatEdit';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useEditChat } from '@repo/api/chat/useEditChat';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { chatEditSchema } from '@repo/schemas/chat/chatEdit';
import { CheckCircle2Icon, LoaderCircleIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the ChatEditName component.
 * @property {number} id - The chat ID to edit.
 * @property {string} name - The current chat name.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface ChatEditNameProps {
  id: number;
  name: string;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function ChatEditName(chatEditNameProps: ChatEditNameProps) {
  const { id, name, open, onOpenChange } = chatEditNameProps;
  const { mutateAsync, isPending } = useEditChat();

  const form = useForm<ChatEditSchema>({
    values: {
      id: id,
      name: name,
    },
    mode: 'onBlur',
    resolver: standardSchemaResolver(chatEditSchema),
  });

  const onClose = () => {
    onOpenChange(false);
    form.reset();
  };

  const onSubmit = (data: ChatEditSchema) => {
    mutateAsync(data)
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'sm:max-w-96'} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
        <Form {...form}>
          <DialogBody>
            {isPending && (
              <div className={'bg-background/75 absolute inset-0 z-60 m-auto flex flex-wrap items-center justify-center gap-3 backdrop-blur-md'}>
                <LoaderCircleIcon className={'animate-spin'} aria-hidden={'true'} />
                <h5 className={'font-medium'}>Trwa zmiana nazwy...</h5>
              </div>
            )}
            <DialogHeader className={'border-border-primary border-b py-5'}>
              <DialogTitle>Edytuj nazwę</DialogTitle>
            </DialogHeader>
            <DialogDescription className={'sr-only'}>Formularz zmiany nazwy chatu</DialogDescription>
            <form id={'chat-edit-name-form'} className={'flex w-full flex-col gap-3 px-3 py-3 md:px-4'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
              <FormField
                name={'name'}
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nazwa</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={'Nazwa konwersacji'} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </DialogBody>
          <DialogFooter className={'border-border-primary border-t'}>
            <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
              Anuluj
            </Button>
            <Button disabled={isPending} form={'chat-edit-name-form'} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
              <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                <CheckCircle2Icon />
                Zapisz
              </LoadingSwap>
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
